import { useState } from "react";
import { useTranslation } from "react-i18next";
import DashboardLayout from "../../components/DashboardLayout/DashboardLayout";
import UsageSummary from "../../components/UsageSummary/UsageSummary";
import { formatNumber } from "../../i18n/format";
import { getApiErrorMessage } from "../../lib/apiError";
import { useBillingCheckoutCreate, useBillingUsageRetrieve } from "../../services/billing/generated/billing/billing";
import type { BillingUsage, CheckoutResponse } from "../../services/billing/generated/model";
import "./PlanUsagePage.css";

/**
 * Company "Plan & usage" page (WP2.4): current plan, credit balance, usage
 * this month vs limits, and credit packs to buy through Stripe Checkout.
 * Credits are added by the Stripe webhook once payment succeeds.
 */
const PlanUsagePage = () => {
  const { t } = useTranslation("b2b");
  const { data, isLoading, error } = useBillingUsageRetrieve();
  const usage = data as BillingUsage | undefined;
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [buyingCode, setBuyingCode] = useState<string | null>(null);

  const checkout = useBillingCheckoutCreate({
    mutation: {
      onSuccess: (resp) => {
        const url = (resp as CheckoutResponse | undefined)?.url;
        if (url) window.location.href = url;
      },
      onError: (err: unknown) => {
        setBuyingCode(null);
        setCheckoutError(getApiErrorMessage(err, t("planUsage.checkoutError")));
      },
    },
  });

  const buy = (code: string) => {
    setCheckoutError(null);
    setBuyingCode(code);
    checkout.mutate({ data: { creditPackCode: code } });
  };

  return (
    <DashboardLayout>
      <div className="plan-usage">
        <header className="plan-usage__header">
          <h1 className="plan-usage__title">{t("planUsage.title")}</h1>
          <p className="plan-usage__subtitle">{t("planUsage.subtitle")}</p>
        </header>

        {isLoading ? <div className="plan-usage__state">{t("planUsage.loading")}</div> : null}
        {error ? (
          <div className="plan-usage__error" role="alert">
            {getApiErrorMessage(error, t("planUsage.loadError"))}
          </div>
        ) : null}

        {usage ? (
          <>
            <section className="plan-usage__card">
              <UsageSummary usage={usage} />
            </section>

            <section className="plan-usage__card">
              <h2 className="plan-usage__card-title">{t("planUsage.buyTitle")}</h2>
              <p className="plan-usage__hint">{t("planUsage.buyHint")}</p>
              {checkoutError ? (
                <div className="plan-usage__error" role="alert">
                  {checkoutError}
                </div>
              ) : null}
              {usage.creditPacks.length === 0 ? (
                <p className="plan-usage__state">{t("planUsage.noPacks")}</p>
              ) : (
                <ul className="plan-usage__packs">
                  {usage.creditPacks.map((pack) => (
                    <li key={pack.code} className="plan-usage__pack">
                      <span className="plan-usage__pack-name">{pack.name}</span>
                      <span className="plan-usage__pack-credits">
                        {t("planUsage.packCredits", { credits: formatNumber(pack.credits) })}
                      </span>
                      <button
                        type="button"
                        className="plan-usage__buy"
                        onClick={() => buy(pack.code)}
                        disabled={checkout.isPending}
                      >
                        {buyingCode === pack.code && checkout.isPending
                          ? t("planUsage.redirecting")
                          : usage.billingCurrency
                            ? t("planUsage.buyIn", { currency: usage.billingCurrency })
                            : t("planUsage.buy")}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        ) : null}
      </div>
    </DashboardLayout>
  );
};

export default PlanUsagePage;
